class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => (evt.code === 'Escape' || evt.key === 'Escape') && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.onDocumentKeydown = (event) => {
      if (!(this.classList.contains('active') || this.openTimer)) return;
      if (event.key === 'Escape' && !event.target.closest?.('dialog[open]')) {
        event.preventDefault();
        this.close();
      }
    };
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !this.classList.contains('active')) return;
      const panel = this.querySelector('.drawer__inner');
      const controls = getFocusableElements(panel).filter((element) => !element.disabled
        && !element.closest('[hidden]') && element.getClientRects().length
        && getComputedStyle(element).visibility === 'visible');
      if (!controls.length) { event.preventDefault(); panel.focus(); return; }
      const current = controls.indexOf(document.activeElement);
      const next = current < 0 ? (event.shiftKey ? controls.length - 1 : 0)
        : (current + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
      event.preventDefault();
      controls[next].focus();
    });
  }

  connectedCallback() { document.addEventListener('keydown', this.onDocumentKeydown); }

  disconnectedCallback() { document.removeEventListener('keydown', this.onDocumentKeydown); }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) {
      // An in-drawer add replaces the clicked row. Rebind the native trap when
      // that disconnected control leaves focus on the page behind the drawer.
      const panel = this.querySelector('.drawer__inner');
      if (panel && !this.contains(document.activeElement)) {
        const container = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer');
        trapFocus(container || panel, panel);
      }
      return;
    }
    if (this.openTimer) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Separate the visibility change from the transition, but do not depend on
    // transitionend for keyboard focus: rapid re-opens may have no transition.
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.classList.add('animate', 'active');
      const focusWhenVisible = (attempts) => {
        this.focusTimer = null;
        if (!this.classList.contains('active')) return;
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        // The panel can still compute as hidden for a frame (or through the
        // closing visibility transition) even after the active class lands.
        if (getComputedStyle(focusElement).visibility !== 'visible') {
          if (attempts < 40) this.focusTimer = setTimeout(() => focusWhenVisible(attempts + 1), 50);
          return;
        }
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer');
        trapFocus(containerToTrapFocusOn || focusElement, focusElement);
      };
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = null;
        focusWhenVisible(0);
      });
    });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    clearTimeout(this.renderTimer);
    this.renderTimer = null;
    clearTimeout(this.addAnnouncementTimer);
    this.clearAddedState();
    clearTimeout(this.openTimer);
    this.openTimer = null;
    clearTimeout(this.focusTimer);
    this.focusTimer = null;
    cancelAnimationFrame(this.focusFrame);
    this.focusFrame = null;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble'));
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.clearAddedState();
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // The swapped markup is inert: its page controls stay disabled until their
    // owning runtime re-binds them. Announce the replacement so a listener can
    // re-initialise this drawer without a navigation. No hard dependency here;
    // the commerce runtime guards its own re-entry per root.
    this.dispatchEvent(new CustomEvent('miniami:cart-drawer-rendered', { bubbles: true }));

    clearTimeout(this.renderTimer);
    this.renderTimer = setTimeout(() => {
      this.renderTimer = null;
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.classList.toggle('is-empty', !!this.querySelector('.drawer__inner-empty'));
      this.open();
      if (parsedState.id || parsedState.items?.length || parsedState.announceAdded === true) this.announceAdd(parsedState);
    });
  }

  // Rebind native controls after a whole-root cart snapshot, without opening a
  // closed drawer or leaving the focus trap attached to disconnected markup.
  syncContents() {
    this.clearAddedState();
    this.classList.toggle('is-empty', !!this.querySelector('.drawer__inner-empty'));
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    if (this.classList.contains('active')) {
      const panel = this.querySelector('.drawer__inner');
      if (panel) trapFocus(this.querySelector('.drawer__inner-empty') || panel, panel);
    }
  }

  clearAddedState() {
    delete this.dataset.cartState;
    const status = this.querySelector('#CartDrawer-AddStatus');
    if (status) { status.textContent = ''; status.hidden = true; }
    const moment = this.querySelector('[data-cart-just-added]');
    if (moment) { moment.replaceChildren(); moment.hidden = true; }
    const heading = this.querySelector('[data-cart-heading]');
    if (heading) heading.hidden = false;
    const panel = this.querySelector('.drawer__inner');
    if (panel && heading) panel.setAttribute('aria-labelledby', heading.id);
  }

  // Compact successful-add feedback; the native list is the only item display.
  announceAdd(state = {}) {
    const status = this.querySelector('#CartDrawer-AddStatus');
    if (!status) return;
    clearTimeout(this.addAnnouncementTimer);
    status.textContent = status.dataset.addedLabel || 'Added to your cart';
    status.hidden = false;
    this.dataset.cartState = 'postadd';
    const heading = this.querySelector('[data-cart-heading]');
    if (heading) heading.hidden = false;
    if (heading) this.querySelector('.drawer__inner')?.setAttribute('aria-labelledby', heading.id);
    const added = state.items?.[0] || state;
    const rows = [...this.querySelectorAll('.cart-item[data-line-key]')];
    let matches = added.key ? rows.filter(row => row.dataset.lineKey === String(added.key)) : [];
    if (!matches.length && (added.variant_id || added.id)) matches = rows.filter(row => row.dataset.variantId === String(added.variant_id || added.id));
    if (matches.length !== 1) return; // Never choose an unrelated/ambiguous line.
    const row = matches[0];
    const title = row.querySelector('.cart-item__name');
    if (title) status.textContent += ` · ${title.textContent.trim()}`;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    this.cartUpdateUnsubscriber?.();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Successful add renderContents replaces the WHOLE drawer. A second
      // asynchronous partial refresh must not erase its just-added moment.
      if ((event.source === 'cart-items' && event.sectionsRendered)
        || ['product-form', 'miniami-parallel-bundle'].includes(event.source)) return;
      return this.onCartUpdate(event);
    });
  }

  updateQuantity(...args) {
    this.closest('cart-drawer')?.clearAddedState();
    return super.updateQuantity(...args);
  }

  getSectionsToRender() {
    return super.getSectionsToRender();
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
